// Side-by-side layout for overlapping calendar events.
//
// Every chip used to be rendered `absolute left-0.5 right-0.5`, so two events at
// the same time sat exactly on top of each other and only the last one drawn was
// visible. This is the standard interval-graph column assignment Google Calendar
// uses: cluster transitively-overlapping events, greedily pack each cluster into
// columns, then let every event expand rightwards into columns nothing blocks —
// which is what stops a lone event next to a busy cluster from rendering as a
// needlessly thin sliver.

const MIN_LAYOUT_MINUTES = 15 // treat sub-15-minute events as 15 for collision purposes

/**
 * @param {Array<{key: string, startMinutes: number, endMinutes: number}>} events
 * @returns {Array<{key: string, columnIndex: number, columnCount: number, span: number,
 *                  leftPercent: number, widthPercent: number}>}
 *   Positions keyed back to the input `key`; input objects are never mutated.
 */
export function layoutDayEvents(events) {
  if (!events?.length) return []

  // Longer events first among equal starts, so the big block takes the left
  // column and shorter ones stack to its right — matching Google's reading order.
  const sorted = [...events].sort((a, b) => (
    a.startMinutes - b.startMinutes ||
    (b.endMinutes - b.startMinutes) - (a.endMinutes - a.startMinutes) ||
    String(a.key).localeCompare(String(b.key))
  ))

  const collisionEnd = (e) => Math.max(e.endMinutes, e.startMinutes + MIN_LAYOUT_MINUTES)

  // 1. Cluster: a new cluster starts where an event begins at or after the
  //    furthest end seen so far, i.e. nothing before it can overlap it.
  const clusters = []
  let current = []
  let currentEnd = -Infinity

  for (const event of sorted) {
    if (current.length && event.startMinutes >= currentEnd) {
      clusters.push(current)
      current = []
      currentEnd = -Infinity
    }
    current.push(event)
    currentEnd = Math.max(currentEnd, collisionEnd(event))
  }
  if (current.length) clusters.push(current)

  // 2. Within each cluster, pack into the leftmost column that is free, then
  //    widen each event across the columns to its right that stay free.
  const positions = []

  for (const cluster of clusters) {
    const columns = []          // columns[i] = events placed in that column
    const columnOf = new Map()  // event -> column index

    for (const event of cluster) {
      let placed = false
      for (let i = 0; i < columns.length; i++) {
        const last = columns[i][columns[i].length - 1]
        if (collisionEnd(last) <= event.startMinutes) {
          columns[i].push(event)
          columnOf.set(event, i)
          placed = true
          break
        }
      }
      if (!placed) {
        columns.push([event])
        columnOf.set(event, columns.length - 1)
      }
    }

    const columnCount = columns.length

    for (const event of cluster) {
      const columnIndex = columnOf.get(event)
      let span = 1

      for (let i = columnIndex + 1; i < columnCount; i++) {
        const blocked = columns[i].some(other =>
          other !== event &&
          other.startMinutes < collisionEnd(event) &&
          collisionEnd(other) > event.startMinutes
        )
        if (blocked) break
        span++
      }

      positions.push({
        key:          event.key,
        columnIndex,
        columnCount,
        span,
        leftPercent:  (columnIndex / columnCount) * 100,
        widthPercent: (span / columnCount) * 100,
      })
    }
  }

  return positions
}

/**
 * Expands bookings into per-day segments, clamped to each day they touch, so a
 * timed event crossing midnight renders on every day it covers instead of only
 * its start day (where its height also used to run past the bottom of the grid).
 *
 * @param {Array} bookings           raw booking objects
 * @param {object} helpers
 * @param {(d: Date) => string} helpers.dayKey
 * @param {(d: Date) => number} helpers.minutesOfDay
 * @returns {Map<string, Array>} dayKey -> segments, each
 *   { booking, key, startMinutes, endMinutes, continuesBefore, continuesAfter }
 */
export function segmentBookingsByDay(bookings, { dayKey, minutesOfDay }) {
  const byDay = new Map()
  const DAY_MINUTES = 24 * 60

  for (const booking of bookings) {
    if (booking.all_day) continue

    const start = new Date(booking.start_at ?? booking.start)
    let   end   = new Date(booking.end_at   ?? booking.end)
    if (Number.isNaN(start.getTime())) continue
    // A missing or inverted end still deserves a visible chip.
    if (Number.isNaN(end.getTime()) || end <= start) end = new Date(start.getTime() + 30 * 60000)

    const startKey = dayKey(start)
    const endKey   = dayKey(end)

    // Walk day by day from the start, anchored at noon so a DST shift cannot
    // skip or repeat a day.
    let cursor    = new Date(start)
    let cursorKey = startKey
    let guard     = 0

    while (guard++ < 400) {
      const isFirst = cursorKey === startKey
      const isLast  = cursorKey === endKey

      const startMinutes = isFirst ? minutesOfDay(start) : 0
      const endMinutes   = isLast  ? minutesOfDay(end)   : DAY_MINUTES

      // An event ending exactly at midnight belongs to the previous day only.
      if (!(isLast && endMinutes === 0 && !isFirst)) {
        const segments = byDay.get(cursorKey) ?? []
        segments.push({
          booking,
          key:             isFirst && isLast ? booking.id : `${booking.id}:${cursorKey}`,
          startMinutes,
          endMinutes:      Math.max(endMinutes, startMinutes + 1),
          continuesBefore: !isFirst,
          continuesAfter:  !isLast,
        })
        byDay.set(cursorKey, segments)
      }

      if (isLast) break

      const noon = new Date(cursor)
      noon.setHours(12, 0, 0, 0)
      cursor = new Date(noon.getTime() + 24 * 3600000)
      const nextKey = dayKey(cursor)
      if (nextKey === cursorKey) break // defensive: never loop on the same day
      cursorKey = nextKey
    }
  }

  return byDay
}

// ── All-day events ───────────────────────────────────────────────────────────
//
// All-day events are date-based, not instant-based: Google stores them as a
// start date and an *exclusive* end date, so a single-day event on the 27th
// arrives as 27th to 28th. Comparing them as instants against a column's local
// midnight rendered every all-day event a day too long, and got worse the
// further the viewer sat from UTC.

function utcDateKey(date) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

function nextDateKey(key) {
  const d = new Date(`${key}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + 1)
  return utcDateKey(d)
}

/**
 * The calendar dates an all-day booking covers, as `from` (inclusive) and
 * `toExclusive`, both 'YYYY-MM-DD'.
 */
export function allDaySpan(booking) {
  const start = new Date(booking.start_at ?? booking.start)
  const end   = new Date(booking.end_at   ?? booking.end)

  const from = utcDateKey(start)
  let toExclusive = Number.isNaN(end.getTime()) ? from : utcDateKey(end)

  // Defend against a provider that sends an inclusive or missing end: every
  // all-day event covers at least its own day.
  if (toExclusive <= from) toExclusive = nextDateKey(from)

  return { from, toExclusive }
}

/** Does an all-day booking cover the given 'YYYY-MM-DD' column? */
export function coversAllDay(booking, dayKey) {
  const { from, toExclusive } = allDaySpan(booking)
  return dayKey >= from && dayKey < toExclusive
}

/**
 * Collapses the same all-day event appearing on several connected calendars.
 *
 * A public holiday subscribed on three calendars arrives as three rows, and the
 * all-day strip stacked all three, pushing the real events out of view. They are
 * the same event to a reader, so only the first is kept.
 *
 * Matched on title and the dates covered, not on id: each calendar's copy has
 * its own external id, which is exactly why they were not already collapsing.
 */
export function dedupeAllDay(bookings) {
  const seen = new Set()

  return bookings.filter(booking => {
    const title = (booking.title ?? booking.name ?? '').trim().toLowerCase()
    // An untitled chip carries nothing to compare, so leave it alone rather
    // than collapsing unrelated events onto one another.
    if (!title) return true

    const { from, toExclusive } = allDaySpan(booking)
    const key = `${title}|${from}|${toExclusive}`
    if (seen.has(key)) return false

    seen.add(key)
    return true
  })
}
